import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Clock, History, X, FileText, FileQuestion, PhoneCall, Building2, CalendarClock, ShoppingCart, Users2, FolderKanban, Truck } from 'lucide-react';
import { useActivityBar } from '../contexts/ActivityBarContext';

const OPEN_COLLAPSED_KEY = 'c30.activity_bar.open_collapsed';
const RECENT_COLLAPSED_KEY = 'c30.activity_bar.recent_collapsed';

// Each Activity Bar tab shows the icon of the modal it represents (not a
// generic clock/history icon) so it's instantly recognizable at a glance.
const TYPE_ICONS = {
  proforma: FileText,
  quote: FileQuestion,
  eight_by_eight_calls: PhoneCall,
  add_to_crm: Building2,
  add_call_to_crm: PhoneCall,
  schedule_call: CalendarClock,
  stock_request: ShoppingCart,
  customer_management: Users2,
  project_management: FolderKanban,
  b29_transfer: Truck,
};

/**
 * Global "Activity Bar" — sits directly under the sub-header so users can
 * multitask across the site. Only rendered when Modal UI v2 is on (see
 * useUIPreview) and there's at least one tab.
 * Left = "Open Activities" (unfinished modals, e.g. a half-done P.O.,
 * navigated away from before completing — up to 3, oldest auto-dropped).
 * Right = "Recent Activities" (last 3 read-only views — a quote, call,
 * project, customer — for a quick jump back, logged the moment they're
 * opened).
 * The "OPEN" / "RECENT" labels are themselves the toggle — clicking either
 * slides that side's tabs away (hide) or back in (show), independently of
 * one another. Highlights orange while its own tabs are hidden. The
 * collapsed state is saved per-user and applies everywhere EXCEPT Transport
 * (`/transport`), which always starts both sides hidden on every visit.
 *
 * Look: every tab is a fully rounded pill (rounded on both sides), slightly
 * shorter than before, and tabs squeeze (their titles truncate) to share the
 * space instead of being cut off mid-tab at the edge of their group.
 * `glassTheme` follows the sub-header: 'dark' gives dark glass with white
 * text (OPEN / RECENT pills and the tabs), 'light' keeps the white tabs.
 * `glassSolid` is dark glass sitting on a LIGHT page (the dark sub-header
 * style chosen on a light page), which needs denser glass so the white text
 * does not wash out.
 */
export const ActivityBar = ({ dimmed = false, glassTheme = 'light', glassSolid = false }) => {
  const { openActivities, recentActivities, reopenOpen, reopenRecent, removeOpen } = useActivityBar();
  const location = useLocation();
  const isTransportPage = location.pathname === '/transport';

  const [openCollapsedPref, setOpenCollapsedPref] = useState(() => localStorage.getItem(OPEN_COLLAPSED_KEY) === '1');
  const [recentCollapsedPref, setRecentCollapsedPref] = useState(() => localStorage.getItem(RECENT_COLLAPSED_KEY) === '1');
  useEffect(() => { localStorage.setItem(OPEN_COLLAPSED_KEY, openCollapsedPref ? '1' : '0'); }, [openCollapsedPref]);
  useEffect(() => { localStorage.setItem(RECENT_COLLAPSED_KEY, recentCollapsedPref ? '1' : '0'); }, [recentCollapsedPref]);

  // Transport's own session-only override — resets to "hidden" every time the
  // user lands on /transport, regardless of what's saved for other pages.
  const [transportOpenRevealed, setTransportOpenRevealed] = useState(false);
  const [transportRecentRevealed, setTransportRecentRevealed] = useState(false);
  useEffect(() => {
    if (isTransportPage) { setTransportOpenRevealed(false); setTransportRecentRevealed(false); }
  }, [isTransportPage]);

  if (openActivities.length === 0 && recentActivities.length === 0) return null;
  // A Modal UI v2 activity/modal is open — its overlay starts right where the
  // sub-header ends (ignoring this bar's own height, by design), and this bar
  // lives inside the header's z-50 stacking context, ABOVE the modal's z-45
  // overlay. Merely dimming (opacity) still left the tabs visibly painted on
  // top of the modal's rounded top edge. Hide entirely instead — the modal is
  // meant to fully cover this band while it's open.
  if (dimmed) return null;

  const openCollapsed = isTransportPage ? !transportOpenRevealed : openCollapsedPref;
  const recentCollapsed = isTransportPage ? !transportRecentRevealed : recentCollapsedPref;
  const toggleOpen = () => { if (isTransportPage) setTransportOpenRevealed((v) => !v); else setOpenCollapsedPref((v) => !v); };
  const toggleRecent = () => { if (isTransportPage) setTransportRecentRevealed((v) => !v); else setRecentCollapsedPref((v) => !v); };

  // ── Look ────────────────────────────────────────────────────────────────────────────
  const D = glassTheme === 'dark';
  // Dark glass: frosted white-on-dark, with a soft inner highlight and depth (same finish as the header buttons).
  const darkGlassBg = glassSolid
    ? 'bg-[rgba(38,40,47,0.92)] hover:bg-[rgba(56,58,66,0.96)]'
    : 'bg-white/[0.09] hover:bg-white/[0.17]';
  const darkGlass = `${darkGlassBg} border border-white/[0.18] hover:border-white/30 text-white backdrop-blur-md backdrop-saturate-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.20),0_4px_12px_rgba(0,0,0,0.35)]`;
  // Tabs: rounded on both sides, slightly shorter (was py-1.5 / 12px text), and min-w-0 (no shrink-0) so they squeeze.
  const tabShape = 'rounded-full py-1 text-[11px] leading-4 min-w-0 transition-colors';
  const openTabClass = D
    ? `group flex items-center gap-1.5 pl-3 pr-2 font-semibold ${tabShape} ${darkGlass}`
    : `group flex items-center gap-1.5 pl-3 pr-2 font-semibold ${tabShape} bg-white text-gray-800 shadow-md border border-orange-200 hover:bg-orange-50`;
  const recentTabClass = D
    ? `flex items-center gap-1.5 px-3 font-medium ${tabShape} ${darkGlass}`
    : `flex items-center gap-1.5 px-3 font-medium ${tabShape} bg-white/90 text-gray-700 shadow border border-gray-200 hover:bg-gray-50`;
  // OPEN / RECENT labels: dark glass with white text when expanded; the orange "tabs hidden" highlight is kept.
  const toggleClass = (collapsed) =>
    `text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 mb-0.5 transition-colors ${
      collapsed ? 'text-white shadow-sm' : (D ? darkGlass : 'text-black bg-white shadow-sm')
    }`;

  return (
    <div
      className={`hidden md:flex items-end justify-between w-full px-4 gap-3 pb-1 transition-all duration-300 ${dimmed ? 'opacity-40 grayscale pointer-events-none' : ''}`}
      style={{ background: 'transparent', position: 'relative', zIndex: 39 }}
      data-testid="activity-bar"
      data-c30-activity-glass={glassTheme}
    >
      <div className="flex items-end gap-1.5 overflow-hidden min-w-0 min-h-[30px]">
        {openActivities.length > 0 && (
          <button
            onClick={toggleOpen}
            title={openCollapsed ? 'Show open tabs' : 'Hide open tabs'}
            className={toggleClass(openCollapsed)}
            style={openCollapsed ? { backgroundColor: '#ee7203' } : {}}
            data-testid="activity-bar-open-toggle"
          >
            Open
          </button>
        )}
        <div
          className={`flex items-end gap-1.5 overflow-hidden min-w-0 transition-all duration-300 ${
            openCollapsed ? 'max-w-0 opacity-0 -translate-x-3' : 'max-w-[560px] opacity-100 translate-x-0'
          }`}
          data-testid="activity-bar-open-group"
        >
          {openActivities.map((a) => {
            const Icon = TYPE_ICONS[a.type] || Clock;
            return (
              <button
                key={a.id}
                onClick={() => reopenOpen(a.id)}
                title={a.subtitle || a.title}
                className={openTabClass}
                data-testid={`activity-bar-open-tab-${a.id}`}
              >
                <Icon className={`w-3 h-3 shrink-0 ${D ? 'text-[#ff9838]' : 'text-orange-500'}`} />
                <span className="max-w-[190px] min-w-0 truncate">{a.title}</span>
                <X
                  className={`w-3 h-3 shrink-0 ${D ? 'text-white/50 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                  onClick={(e) => { e.stopPropagation(); removeOpen(a.id); }}
                  data-testid={`activity-bar-open-tab-close-${a.id}`}
                />
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-end gap-1.5 overflow-hidden min-w-0 min-h-[30px]">
        <div
          className={`flex items-end gap-1.5 overflow-hidden min-w-0 transition-all duration-300 ${
            recentCollapsed ? 'max-w-0 opacity-0 translate-x-3' : 'max-w-[560px] opacity-100 translate-x-0'
          }`}
          data-testid="activity-bar-recent-group"
        >
          {recentActivities.map((a) => {
            const Icon = TYPE_ICONS[a.type] || History;
            return (
              <button
                key={a.id}
                onClick={() => reopenRecent(a.id)}
                title={a.subtitle || a.title}
                className={recentTabClass}
                data-testid={`activity-bar-recent-tab-${a.id}`}
              >
                <Icon className={`w-3 h-3 shrink-0 ${D ? 'text-white/60' : 'text-gray-400'}`} />
                <span className="max-w-[190px] min-w-0 truncate">{a.title}</span>
              </button>
            );
          })}
        </div>
        {recentActivities.length > 0 && (
          <button
            onClick={toggleRecent}
            title={recentCollapsed ? 'Show recent tabs' : 'Hide recent tabs'}
            className={toggleClass(recentCollapsed)}
            style={recentCollapsed ? { backgroundColor: '#ee7203' } : {}}
            data-testid="activity-bar-recent-toggle"
          >
            Recent
          </button>
        )}
      </div>
    </div>
  );
};

export default ActivityBar;
